import { ThemeEvents } from '@theme/events';

class AeyaCartRecs extends HTMLElement {
  connectedCallback() {
    this.url = this.dataset.url;
    this.sectionId = this.dataset.sectionId;
    this.intent = this.dataset.intent || 'related';
    this.limit = parseInt(this.dataset.limit ?? '4', 10) || 4;
    this.target = this.querySelector('[data-cart-recs-target]');
    this.arrows = this.querySelector('[data-cart-recs-arrows]');
    this.fallbackHTML = this.target ? this.target.innerHTML : '';

    this.onCartUpdate = () => this.load();
    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    this.addEventListener('click', this.onArrowClick);
    if (this.target) this.target.addEventListener('scroll', this.updateArrows, { passive: true });
    window.addEventListener('resize', this.updateArrows);

    this.updateArrows();
    this.load();
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.removeEventListener('resize', this.updateArrows);
  }

  onArrowClick = (event) => {
    const arrow = event.target.closest('.aeya-cart-recs__arrow');
    if (!arrow || !this.target) return;
    const card = this.target.querySelector('.aeya-cart-rec');
    const step = card ? card.getBoundingClientRect().width + 12 : this.target.clientWidth;
    this.target.scrollBy({ left: arrow.dataset.dir === 'prev' ? -step : step, behavior: 'smooth' });
  };

  updateArrows = () => {
    if (!this.arrows || !this.target) return;
    const overflow = this.target.scrollWidth - this.target.clientWidth > 1;
    this.arrows.hidden = !overflow;
    if (!overflow) return;
    const max = this.target.scrollWidth - this.target.clientWidth - 1;
    const prev = this.arrows.querySelector('[data-dir="prev"]');
    const next = this.arrows.querySelector('[data-dir="next"]');
    if (prev) prev.disabled = this.target.scrollLeft <= 1;
    if (next) next.disabled = this.target.scrollLeft >= max;
  };

  async load() {
    if (this.loading || !this.target) return;
    this.loading = true;
    try {
      const cart = await this.getCart();
      const items = cart?.items ?? [];
      const inCart = new Set(items.map((item) => String(item.product_id)));

      let cards = await this.fetchRecommendations(items);
      if (!cards.length) cards = this.fallbackCards();
      cards = cards.filter((card) => !inCart.has(card.dataset.productId)).slice(0, this.limit);

      if (!cards.length) return this.hide();
      this.target.replaceChildren(...cards);
      this.hidden = false;
      this.updateArrows();
    } catch {
      this.hide();
    } finally {
      this.loading = false;
    }
  }

  async fetchRecommendations(items) {
    if (!items.length) return [];
    const seed = items[items.length - 1].product_id;
    const query = `section_id=${encodeURIComponent(this.sectionId)}&product_id=${seed}&intent=${this.intent}&limit=${this.limit + items.length}`;
    const res = await fetch(`${this.url}?${query}`, { credentials: 'same-origin' });
    if (!res.ok) return [];
    const parsed = new DOMParser().parseFromString(await res.text(), 'text/html');
    const list = parsed.querySelector('[data-cart-recs-list]');
    return list ? Array.from(list.querySelectorAll('[data-product-id]')) : [];
  }

  fallbackCards() {
    const tmp = document.createElement('div');
    tmp.innerHTML = this.fallbackHTML;
    return Array.from(tmp.querySelectorAll('[data-product-id]'));
  }

  async getCart() {
    try {
      const res = await fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
      return res.ok ? await res.json() : null;
    } catch {
      return null;
    }
  }

  hide() {
    this.hidden = true;
  }
}

if (!customElements.get('aeya-cart-recs')) {
  customElements.define('aeya-cart-recs', AeyaCartRecs);
}
